/**
 * scene.ts — "Crayon Fairway".
 *
 * Untextured flat-colour low-poly. One directional sun, one hemisphere fill,
 * blob shadows, distance fog. No PBR, no textures, no loaded assets: every
 * vertex and every colour in here is generated in code.
 *
 * Everything is built from the 'three/webgpu' entry point with node materials,
 * so the identical code path runs on both the WebGPU and the WebGL2 backends.
 *
 * Coordinate convention (types.ts): +X lateral, +Y up, +Z downrange. Tee at
 * the origin. Metres, seconds, radians.
 */

import * as THREE from 'three/webgpu'
import { TEE_R } from './ballistics'
import { makeRng } from './rng'
import {
	BALL_R,
	BALL_REST_Y,
	CLUB_FACE_H,
	CLUB_FACE_W,
	CLUB_LEN,
	COURSE,
	CRACKED_AT,
	CUP_R,
	HP_MAX,
	MAX_ANGER,
	PIVOT,
	SCUFFED_AT,
	SWEET_Y,
	SWING_ANGLE_IMPACT,
	TEE_TOP_Y,
} from './tuning'
import type { GolferSpec, HoleSpec, Tree } from './types'

// ── Palette. Belongs in tuning.ts later. ─────────────────────────────────────
//
// `flag` is reserved: the flagstick banner and the cup rim are the only things
// in the whole scene allowed to be that red, so the eye always knows where the
// hole is.

export const PALETTE = {
	skyTop: 0x87c5eb,
	skyLow: 0xffd9a0,
	fairway: 0x8fbf6b,
	rough: 0x6e9e52,
	sand: 0xefd9a7,
	ball: 0xfbf6ea,
	flag: 0xe85d4a,
	ink: 0x35404a,
} as const

/** Every art number the game does not already own. Belongs in tuning.ts later. */
export const ART = {
	// sky + weather
	skyRadius: 170,
	skyTopDusk: 0x4a6ba0,
	skyLowDusk: 0xff9a55,
	fogNearDay: 150,
	fogFarDay: 620,
	fogNearDusk: 118,
	fogFarDusk: 470,
	sunIntensityDay: 2.15,
	sunIntensityDusk: 1.42,
	hemiIntensityDay: 1.35,
	hemiIntensityDusk: 0.92,
	sunColorDay: 0xfff4e0,
	sunColorDusk: 0xffab63,
	// derived greens and ground
	fairwayStripe: 0x97c673,
	green: 0xa5d17e,
	teeMat: 0x9dcb7b,
	markerBand: 0x7fb15e,
	obGround: 0x5b8746,
	water: 0x5fa5d2,
	waterEdge: 0x8dc8e6,
	cartPath: 0xa9b2b8,
	// props
	skin: 0xf0c9a4,
	skinFlush: 0xd4586a,
	trousers: 0xd8c08a,
	shoe: 0x2a333c,
	club: 0xb6bec6,
	ballDirty: 0xb4aa98,
	scuff: 0x8d8677,
	trunk: 0x6b4a33,
	canopy: 0x4c8a42,
	canopyLit: 0x5fa153,
	// blob shadows
	shadowY: 0.02,
	shadowOpacity: 0.3,
	// range dressing
	fairwayZ0: -10,
	fairwayZ1: 340,
	stripeLen: 9,
	markerEvery: 50,
	markerLast: 300,
	flagHeight: 2.7,
	/** Widest sideways step one corridor z-slice may take on a bending hole. */
	bendStep: 0.15,
	/** Stacked frustums per canopy. */
	treeTiers: 3,
	/** Wind speed at which the banner streams flat out, m/s. */
	windFull: 6,
	/** Radians a dead-calm banner hangs down the pole. */
	flagDroop: 1,
	// effects
	particlePool: 340,
	trailPoints: 56,
	trailWidth: 0.28,
	steamPeriod: 0.16,
} as const

/**
 * Where the drawn clubface has to sit on the swing arc.
 *
 * The strike resolver is authoritative and it compares the ball centre against
 * a sweet spot pinned at (0, SWEET_Y, 0). So the face we draw must be the point
 * of the arc about PIVOT that arrives at (0, SWEET_Y, 0) exactly when the swing
 * angle is SWING_ANGLE_IMPACT — that is the inverse of the rotation about +X
 * applied to (0, SWEET_Y - PIVOT.y, -PIVOT.z).
 *
 * It lands a little short of CLUB_LEN; the remainder is the club's sole, which
 * we deliberately do not draw below the face so that a full crouch stays clear.
 * Belongs in tuning.ts later.
 */
const IMPACT_COS = Math.cos(SWING_ANGLE_IMPACT)
const IMPACT_SIN = Math.sin(SWING_ANGLE_IMPACT)
export const FACE_LOCAL_Y = (SWEET_Y - PIVOT.y) * IMPACT_COS + -PIVOT.z * IMPACT_SIN
export const FACE_LOCAL_Z = -(SWEET_Y - PIVOT.y) * IMPACT_SIN + -PIVOT.z * IMPACT_COS

export type BurstKind = 'flush' | 'good' | 'thin' | 'shank' | 'turf' | 'splash' | 'sparks'

export type GolferRig = {
	root: THREE.Object3D
	setSpec(spec: GolferSpec): void
	setSwingAngle(a: number): void
	setAnger(tier: number): void
	dropHat(at: { x: number; z: number }): void
	resetProps(): void
	setTantrum(t01: number): void
}

export type Handles = {
	scene: THREE.Scene
	ball: THREE.Object3D
	golfer: GolferRig
	flag: THREE.Object3D
	/**
	 * Tear the last hole down and draw this one. Everything the ground is made
	 * of comes off the HoleSpec, so the drawn course and `surfaceAt` can never
	 * drift apart.
	 */
	buildHole(h: HoleSpec): void
	/**
	 * Move the whole striking rig — golfer and tee peg — to where the ball is
	 * lying. The hole is played out, so the swing happens wherever the last shot
	 * finished, and the tee peg only exists for the first one.
	 */
	setRigAt(x: number, z: number, onTee: boolean): void
	setBallPos(x: number, y: number, z: number): void
	setBallSpin(spinRad: number, axis: { x: number; y: number; z: number }): void
	setBallSquash(amount: number): void
	setBallCondition(hp01: number): void
	/** A permanent gold scuff, carried by a ball that aced and kept rolling. */
	setBallGold(on: boolean): void
	setDaylight(t01: number): void
	setTrailVisible(v: boolean): void
	pushTrail(x: number, y: number, z: number): void
	clearTrail(): void
	burst(kind: BurstKind, x: number, y: number, z: number): void
	stepEffects(dtUnscaled: number): void
}

// ── Small shared helpers ─────────────────────────────────────────────────────

/** Cosmetic randomness only. Kept off the sim's seeded stream on purpose so
 *  particles can never shift a replay. */
const rng = makeRng(0x5eed1e)

const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v)
const clamp01 = (v: number) => clamp(v, 0, 1)
const mix = (a: number, b: number, t: number) => a + (b - a) * t
const DEG = Math.PI / 180

/**
 * A tree's canopy, copied line for line off `treeHit` in ballistics.ts: bare
 * trunk to CANOPY_FROM of its height, then widening to 3.2r at the top. The
 * flight model is the authority, so the drawn tree and the tree the ball hits
 * have to be the same tree.
 */
const CANOPY_FROM = 0.35
const canopyR = (t: Tree, y: number) =>
	t.r * (1 + 2.2 * clamp01((y - t.h * CANOPY_FROM) / (t.h * (1 - CANOPY_FROM))))

const _color = new THREE.Color()
const _colorB = new THREE.Color()
const _vec = new THREE.Vector3()
const _vecB = new THREE.Vector3()
const _quat = new THREE.Quaternion()
const _euler = new THREE.Euler()
const _mat = new THREE.Matrix4()
const _scale = new THREE.Vector3()
const UP = new THREE.Vector3(0, 1, 0)
/** The mark of a ball that got what it wanted and went back out anyway. */
const GOLD = new THREE.Color(0xd8a53f)

function lambert(color: number, flat = true): THREE.MeshLambertNodeMaterial {
	return new THREE.MeshLambertNodeMaterial({ color, flatShading: flat })
}

function basic(color: number, side: THREE.Side = THREE.FrontSide): THREE.MeshBasicNodeMaterial {
	return new THREE.MeshBasicNodeMaterial({ color, side })
}

type Quad = { x0: number; x1: number; z0: number; z1: number; y: number; c: number }

/**
 * Every flat rectangle of the range in one vertex-coloured draw call. Winding
 * is chosen so the geometric normal is +Y.
 */
function quadsMesh(quads: readonly Quad[], mat: THREE.Material): THREE.Mesh {
	const n = quads.length
	const pos = new Float32Array(n * 18)
	const nor = new Float32Array(n * 18)
	const col = new Float32Array(n * 18)
	const vx = [0, 0, 0, 0, 0, 0]
	const vz = [0, 0, 0, 0, 0, 0]
	for (let q = 0; q < n; q++) {
		const d = quads[q]!
		_color.set(d.c)
		vx[0] = d.x0
		vz[0] = d.z0
		vx[1] = d.x0
		vz[1] = d.z1
		vx[2] = d.x1
		vz[2] = d.z1
		vx[3] = d.x0
		vz[3] = d.z0
		vx[4] = d.x1
		vz[4] = d.z1
		vx[5] = d.x1
		vz[5] = d.z0
		for (let k = 0; k < 6; k++) {
			const o = q * 18 + k * 3
			pos[o] = vx[k]!
			pos[o + 1] = d.y
			pos[o + 2] = vz[k]!
			nor[o] = 0
			nor[o + 1] = 1
			nor[o + 2] = 0
			col[o] = _color.r
			col[o + 1] = _color.g
			col[o + 2] = _color.b
		}
	}
	const g = new THREE.BufferGeometry()
	g.setAttribute('position', new THREE.BufferAttribute(pos, 3))
	g.setAttribute('normal', new THREE.BufferAttribute(nor, 3))
	g.setAttribute('color', new THREE.BufferAttribute(col, 3))
	return new THREE.Mesh(g, mat)
}

function disc(r: number, seg: number, x: number, y: number, z: number, mat: THREE.Material) {
	const g = new THREE.CircleGeometry(r, seg)
	g.rotateX(-Math.PI / 2)
	const m = new THREE.Mesh(g, mat)
	m.position.set(x, y, z)
	return m
}

function box(
	w: number,
	h: number,
	d: number,
	x: number,
	y: number,
	z: number,
	mat: THREE.Material,
) {
	const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat)
	m.position.set(x, y, z)
	return m
}

/** A cylinder running from `from` to `to`, both in the parent's local space. */
function strut(
	r0: number,
	r1: number,
	from: THREE.Vector3,
	to: THREE.Vector3,
	mat: THREE.Material,
) {
	const dir = new THREE.Vector3().subVectors(to, from)
	const len = dir.length()
	const g = new THREE.CylinderGeometry(r0, r1, len, 7, 1)
	g.translate(0, len / 2, 0)
	const m = new THREE.Mesh(g, mat)
	m.position.copy(from)
	if (len > 1e-6) m.quaternion.setFromUnitVectors(UP, dir.divideScalar(len))
	return m
}

// ── Burst recipes ────────────────────────────────────────────────────────────

type BurstSpec = {
	n: number
	speed: number
	bias: readonly [number, number, number]
	size: number
	life: number
	grav: number
	colors: readonly number[]
}

const BURSTS: Record<BurstKind, BurstSpec> = {
	flush: {
		n: 26,
		speed: 5.4,
		bias: [0, 1.6, 3.2],
		size: 0.06,
		life: 0.55,
		grav: 9,
		colors: [PALETTE.ball, 0xffffff, PALETTE.sand, ART.fairwayStripe],
	},
	good: {
		n: 16,
		speed: 4.2,
		bias: [0, 1.3, 2.2],
		size: 0.05,
		life: 0.45,
		grav: 9,
		colors: [PALETTE.ball, PALETTE.sand],
	},
	thin: {
		n: 12,
		speed: 3.2,
		bias: [0, 0.9, 1.6],
		size: 0.045,
		life: 0.4,
		grav: 10,
		colors: [PALETTE.fairway, ART.fairwayStripe, PALETTE.ball],
	},
	shank: {
		n: 22,
		speed: 6,
		bias: [2.6, 1.4, 0.4],
		size: 0.055,
		life: 0.6,
		grav: 10,
		colors: [PALETTE.ink, 0x6b7a86, ART.ballDirty],
	},
	turf: {
		n: 20,
		speed: 4.4,
		bias: [0, 3.4, 1.4],
		size: 0.075,
		life: 0.7,
		grav: 12,
		colors: [PALETTE.rough, PALETTE.fairway, 0x4c7238, 0x6b5138],
	},
	splash: {
		n: 30,
		speed: 5.2,
		bias: [0, 6, 0.6],
		size: 0.065,
		life: 0.8,
		grav: 11,
		colors: [ART.water, ART.waterEdge, 0xdff0fa, 0xffffff],
	},
	sparks: {
		n: 18,
		speed: 6.5,
		bias: [0, 2, 2],
		size: 0.032,
		life: 0.35,
		grav: 14,
		colors: [0xffe9b0, 0xfff6d8, 0xb9c2c9],
	},
}

const STEAM_COLORS = [0xe9eff3, 0xd7e3ea, 0xf3f7fa]

/** Body builds, indexed by GolferSpec.build. Belongs in tuning.ts later. */
const BUILDS = [
	{ w: 0.84, d: 0.86, h: 0.95, hip: 0.88 },
	{ w: 1, d: 1, h: 1, hip: 1 },
	{ w: 1.26, d: 1.32, h: 0.97, hip: 1.16 },
] as const

// ── The scene ────────────────────────────────────────────────────────────────

export function buildScene(): Handles {
	const scene = new THREE.Scene()

	const fog = new THREE.Fog(PALETTE.skyLow, ART.fogNearDay, ART.fogFarDay)
	scene.fog = fog
	const clearColor = new THREE.Color(PALETTE.skyLow)
	scene.background = clearColor

	// ── Lights ───────────────────────────────────────────────────────────────

	const sun = new THREE.DirectionalLight(ART.sunColorDay, ART.sunIntensityDay)
	sun.position.set(-46, 86, 40)
	scene.add(sun)
	scene.add(sun.target)

	const hemi = new THREE.HemisphereLight(PALETTE.skyTop, PALETTE.rough, ART.hemiIntensityDay)
	scene.add(hemi)

	// ── Sky dome ─────────────────────────────────────────────────────────────
	//
	// Vertex-coloured so setDaylight can walk the gradient without a texture.
	// It rides with the camera and draws first with depth testing off, so it can
	// never be clipped by an unknown far plane and can never occlude anything.

	const skyGeo = new THREE.SphereGeometry(ART.skyRadius, 24, 16)
	const skyPos = skyGeo.getAttribute('position') as THREE.BufferAttribute
	const skyCol = new THREE.BufferAttribute(new Float32Array(skyPos.count * 3), 3)
	skyGeo.setAttribute('color', skyCol)
	const sky = new THREE.Mesh(
		skyGeo,
		new THREE.MeshBasicNodeMaterial({
			vertexColors: true,
			side: THREE.BackSide,
			fog: false,
			depthTest: false,
			depthWrite: false,
		}),
	)
	sky.frustumCulled = false
	sky.renderOrder = -1000
	sky.onBeforeRender = (_r, _s, camera) => {
		sky.position.copy(camera.position)
		sky.updateWorldMatrix(true, false)
	}
	scene.add(sky)

	// ── The hole ─────────────────────────────────────────────────────────────
	//
	// Everything from here down to the tee peg is rebuilt whenever the round
	// moves on. `surfaceAt` in ballistics.ts is the authority on where the ground
	// is, so every plate below takes its numbers straight off the HoleSpec: what
	// you can see is what the ball will find.

	/**
	 * Draw height per ground plate, in surfaceAt's own precedence order — green
	 * over bunker over water over path over corridor. Greenside sand overlaps the
	 * green on four of the six holes, and the ball plays the green there, so the
	 * green has to be the thing painted on top.
	 */
	const Y = {
		ob: -0.05,
		rough: -0.02,
		fairway: 0,
		marker: 0.004,
		cartpath: 0.006,
		shore: 0.007,
		water: 0.008,
		waterLit: 0.009,
		bunker: 0.011,
		teeMat: 0.012,
		green: 0.014,
		cup: 0.021,
	} as const

	const CUP_DEPTH = 0.62

	const holeRoot = new THREE.Group()
	scene.add(holeRoot)
	// The flagstick's root outlives the hole so the handle stays valid across a
	// round; only what hangs off it is rebuilt.
	const flag = new THREE.Object3D()
	scene.add(flag)

	let banner: THREE.Mesh | null = null
	let bannerPivot: THREE.Object3D | null = null
	let windRad = 0
	let windAmp = 0

	/** Hand back every buffer and material under `root`, then empty it. */
	function disposeSubtree(root: THREE.Object3D): void {
		root.traverse((o) => {
			if (!(o instanceof THREE.Mesh)) return
			o.geometry.dispose()
			const m = o.material
			if (Array.isArray(m)) for (const one of m) one.dispose()
			else m.dispose()
		})
		root.clear()
	}

	function buildHole(h: HoleSpec): void {
		disposeSubtree(holeRoot)
		disposeSubtree(flag)

		// The corridor exists over exactly the span surfaceAt grants it.
		const z0 = -TEE_R
		const z1 = h.pin.z + 90
		const centreAt = (z: number) =>
			h.tee.x + (h.pin.x - h.tee.x) * clamp01(z / Math.max(1, h.pin.z))

		const quads: Quad[] = []

		// Out of bounds: one huge slab that runs past the fog so the horizon is a
		// clean colour join with the sky rather than a visible edge.
		quads.push({ x0: -900, x1: 900, z0: -340, z1: z1 + 420, y: Y.ob, c: ART.obGround })

		// The corridor follows the tee→pin line, so it is cut into z-slices short
		// enough that a bend reads as a curve instead of a staircase. Slices divide
		// the stripe length exactly, which keeps the mowing pattern square.
		const bend = Math.abs(h.pin.x - h.tee.x) / Math.max(1, h.pin.z)
		const perStripe = Math.max(1, Math.ceil((ART.stripeLen * bend) / ART.bendStep))
		const dz = ART.stripeLen / perStripe
		const slices = Math.ceil((z1 - z0) / dz)
		for (let i = 0; i < slices; i++) {
			const a = z0 + i * dz
			const b = Math.min(a + dz, z1)
			const c = centreAt((a + b) / 2)
			quads.push({
				x0: c - h.roughHalfW,
				x1: c + h.roughHalfW,
				z0: a,
				z1: b,
				y: Y.rough,
				c: PALETTE.rough,
			})
			// Two very close greens: it reads as mowing, not as banding.
			quads.push({
				x0: c - h.fairwayHalfW,
				x1: c + h.fairwayHalfW,
				z0: a,
				z1: b,
				y: Y.fairway,
				c: Math.floor(i / perStripe) % 2 === 0 ? PALETTE.fairway : ART.fairwayStripe,
			})
		}

		// Distance bands across the fairway, so a drive can be read at a glance.
		// They stop where the green starts: nothing is measured past the target.
		const lastMarker = h.green.z - h.green.r
		for (let d = ART.markerEvery; d < lastMarker; d += ART.markerEvery) {
			const c = centreAt(d)
			quads.push({
				x0: c - h.fairwayHalfW,
				x1: c + h.fairwayHalfW,
				z0: d - 0.45,
				z1: d + 0.45,
				y: Y.marker,
				c: ART.markerBand,
			})
		}

		// Tee mat.
		quads.push({
			x0: h.tee.x - 1.7,
			x1: h.tee.x + 1.7,
			z0: h.tee.z - 2.4,
			z1: h.tee.z + 1.3,
			y: Y.teeMat,
			c: ART.teeMat,
		})

		// Ponds, each with a sand shore so the edge is legible from the tee and a
		// paler middle so the water is not one dead slab.
		for (const w of h.water) {
			quads.push({
				x0: w.x0 - 2.2,
				x1: w.x1 + 2.2,
				z0: w.z0 - 2.2,
				z1: w.z1 + 2.2,
				y: Y.shore,
				c: PALETTE.sand,
			})
			quads.push({ x0: w.x0, x1: w.x1, z0: w.z0, z1: w.z1, y: Y.water, c: ART.water })
			quads.push({
				x0: mix(w.x0, w.x1, 0.09),
				x1: mix(w.x1, w.x0, 0.09),
				z0: mix(w.z0, w.z1, 0.05),
				z1: mix(w.z1, w.z0, 0.05),
				y: Y.waterLit,
				c: ART.waterEdge,
			})
		}

		const cp = h.cartpath
		if (cp) {
			quads.push({ x0: cp.x0, x1: cp.x1, z0: cp.z0, z1: cp.z1, y: Y.cartpath, c: ART.cartPath })
		}

		holeRoot.add(quadsMesh(quads, new THREE.MeshLambertNodeMaterial({ vertexColors: true })))

		// Bunkers and green are round, so they get their own discs.
		const sandMat = lambert(PALETTE.sand, false)
		for (const b of h.bunkers) holeRoot.add(disc(b.r, 24, b.x, Y.bunker, b.z, sandMat))
		holeRoot.add(disc(h.green.r, 40, h.green.x, Y.green, h.green.z, lambert(ART.green, false)))

		// The cup, with the reserved red painted round its rim.
		//
		// It is a real shaft rather than a dark disc on the grass, because the game's
		// one sincere moment is filmed from the bottom of it: the ceremony camera sits
		// below ground looking up, and a flat disc is invisible from underneath, so
		// the shot that should be dark walls around a circle of sky was just sky.
		const cupRim = new THREE.RingGeometry(CUP_R, CUP_R * 2.1, 20)
		cupRim.rotateX(-Math.PI / 2)
		const cupRimMesh = new THREE.Mesh(cupRim, basic(PALETTE.flag))
		cupRimMesh.position.set(h.pin.x, Y.cup, h.pin.z)
		holeRoot.add(cupRimMesh)

		const cupWall = new THREE.Mesh(
			new THREE.CylinderGeometry(CUP_R, CUP_R, CUP_DEPTH, 20, 1, true),
			basic(0x141a1f, THREE.BackSide),
		)
		cupWall.position.set(h.pin.x, Y.cup + 0.001 - CUP_DEPTH / 2, h.pin.z)
		holeRoot.add(cupWall)
		// Floor sits below the ceremony camera so it never blocks the sky.
		holeRoot.add(disc(CUP_R, 14, h.pin.x, Y.cup - CUP_DEPTH, h.pin.z, basic(0x0d1114)))

		// ── Flagstick ────────────────────────────────────────────────────────

		flag.position.set(h.pin.x, 0, h.pin.z)
		const pole = new THREE.Mesh(
			new THREE.CylinderGeometry(0.045, 0.05, ART.flagHeight, 7),
			lambert(PALETTE.ball, false),
		)
		pole.position.y = ART.flagHeight / 2
		flag.add(pole)

		// A cartoon-scale banner, folded once so it is not a flat card.
		const bannerGeo = new THREE.PlaneGeometry(1.5, 0.95, 3, 1)
		const bp = bannerGeo.getAttribute('position') as THREE.BufferAttribute
		for (let i = 0; i < bp.count; i++) {
			const x = bp.getX(i)
			bp.setZ(i, Math.sin((x + 0.75) * 2.6) * 0.16)
		}
		bannerGeo.computeVertexNormals()
		bannerGeo.translate(0.78, 0, 0)
		banner = new THREE.Mesh(
			bannerGeo,
			new THREE.MeshLambertNodeMaterial({ color: PALETTE.flag, side: THREE.DoubleSide }),
		)
		bannerPivot = new THREE.Object3D()
		bannerPivot.position.y = ART.flagHeight - 0.62
		bannerPivot.add(banner)
		flag.add(bannerPivot)

		// Wind is a real force on the ball now, so the banner has to give it away
		// before the player is hit: it streams the way the wind blows, and how hard
		// it snaps is the speed. Dead calm and it just hangs.
		windRad = h.wind.dirDeg * DEG
		windAmp = clamp01(h.wind.speed / ART.windFull)

		// ── Trees ────────────────────────────────────────────────────────────

		const trunkMat = lambert(ART.trunk, false)
		const canopyDark = lambert(ART.canopy)
		const canopyLit = lambert(ART.canopyLit)
		for (const t of h.trees) {
			// Untapered: the trunk you can see has to be the trunk you can clip.
			const trunkH = t.h * CANOPY_FROM
			const trunk = new THREE.Mesh(new THREE.CylinderGeometry(t.r, t.r, trunkH, 7), trunkMat)
			trunk.position.set(t.x, trunkH / 2, t.z)
			holeRoot.add(trunk)
			// Tiers stacked on the collision profile itself: the canopy is narrowest
			// at the trunk and widest at the top, which is what makes going over a
			// tree a real choice and squeezing under it another one.
			for (let i = 0; i < ART.treeTiers; i++) {
				const y0 = mix(trunkH, t.h, i / ART.treeTiers)
				const y1 = mix(trunkH, t.h, (i + 1) / ART.treeTiers)
				const tier = new THREE.Mesh(
					new THREE.CylinderGeometry(canopyR(t, y1), canopyR(t, y0), y1 - y0, 9),
					i % 2 === 0 ? canopyDark : canopyLit,
				)
				tier.position.set(t.x, (y0 + y1) / 2, t.z)
				tier.rotation.y = i * 0.42
				holeRoot.add(tier)
			}
		}

		// ── Distance markers and OB stakes ───────────────────────────────────

		const postMat = lambert(PALETTE.ink, false)
		const plateMat = lambert(PALETTE.ball, false)
		const postGeo = new THREE.CylinderGeometry(0.11, 0.13, 1.4, 6)
		const bigGeo = new THREE.CylinderGeometry(0.42, 0.42, 0.1, 10)
		const smallGeo = new THREE.CylinderGeometry(0.26, 0.26, 0.08, 8)

		for (let d = ART.markerEvery; d < lastMarker; d += ART.markerEvery) {
			const c = centreAt(d)
			for (const sx of [-1, 1]) {
				const px = c + sx * (h.fairwayHalfW + 1.3)
				const post = new THREE.Mesh(postGeo, postMat)
				post.position.set(px, 0.7, d)
				holeRoot.add(post)
				// One wide disc per hundred metres, one narrow disc per fifty.
				let y = 1.45
				for (let i = 0; i < Math.floor(d / 100); i++) {
					const m = new THREE.Mesh(bigGeo, plateMat)
					m.position.set(px, y, d)
					holeRoot.add(m)
					y += 0.15
				}
				if (d % 100 >= 50) {
					const m = new THREE.Mesh(smallGeo, plateMat)
					m.position.set(px, y, d)
					holeRoot.add(m)
				}
			}
		}

		const stakeGeo = new THREE.CylinderGeometry(0.07, 0.07, 0.9, 5)
		for (let z = 24; z < h.pin.z + 60; z += 28) {
			const c = centreAt(z)
			for (const sx of [-1, 1]) {
				const m = new THREE.Mesh(stakeGeo, postMat)
				m.position.set(c + sx * h.roughHalfW, 0.45, z)
				holeRoot.add(m)
			}
		}
	}

	// ── Tee peg ──────────────────────────────────────────────────────────────

	const peg = new THREE.Mesh(
		new THREE.CylinderGeometry(0.026, 0.012, TEE_TOP_Y, 6),
		lambert(PALETTE.ball, false),
	)
	peg.position.y = TEE_TOP_Y / 2
	scene.add(peg)

	// ── Blob shadows ─────────────────────────────────────────────────────────

	const blobGeo = new THREE.CircleGeometry(1, 20)
	blobGeo.rotateX(-Math.PI / 2)

	function blob(opacity: number): THREE.Mesh {
		const m = new THREE.Mesh(
			blobGeo,
			new THREE.MeshBasicNodeMaterial({
				color: PALETTE.ink,
				transparent: true,
				opacity,
				depthWrite: false,
				fog: false,
			}),
		)
		m.position.y = ART.shadowY
		return m
	}

	const ballShadow = blob(ART.shadowOpacity)
	scene.add(ballShadow)
	const ballShadowMat = ballShadow.material as THREE.MeshBasicNodeMaterial

	// Sun-driven blob skew, refreshed by setDaylight.
	let shadowDirX = 0.7
	let shadowDirZ = 0.7
	let shadowSkew = 0.06
	let shadowStretch = 1

	// ── Ball ─────────────────────────────────────────────────────────────────
	//
	// squash is the parent of spin so the squash axis stays world-up while the
	// ball tumbles. The face has eyes and brows and NO MOUTH: a ball that
	// physically cannot smirk can never break the game's sincerity.

	const ballRoot = new THREE.Object3D()
	ballRoot.position.set(0, BALL_REST_Y, 0)
	scene.add(ballRoot)
	const ballSquash = new THREE.Object3D()
	ballRoot.add(ballSquash)
	const ballSpin = new THREE.Object3D()
	ballSquash.add(ballSpin)

	const ballMat = lambert(PALETTE.ball)
	ballSpin.add(new THREE.Mesh(new THREE.IcosahedronGeometry(BALL_R, 2), ballMat))

	const faceMat = basic(PALETTE.ink)
	const eyeGeo = new THREE.SphereGeometry(BALL_R * 0.22, 7, 5)
	const browGeo = new THREE.BoxGeometry(BALL_R * 0.54, BALL_R * 0.1, BALL_R * 0.1)
	const brows: THREE.Mesh[] = []
	for (const s of [-1, 1]) {
		const eye = new THREE.Mesh(eyeGeo, faceMat)
		eye.position.set(s * BALL_R * 0.36, BALL_R * 0.2, -BALL_R * 0.9)
		eye.scale.set(1, 1.15, 0.7)
		ballSpin.add(eye)

		const brow = new THREE.Mesh(browGeo, faceMat)
		brow.position.set(s * BALL_R * 0.37, BALL_R * 0.52, -BALL_R * 0.83)
		brow.rotation.set(-0.5, 0, s * 0.3)
		ballSpin.add(brow)
		brows.push(brow)
	}

	// Scuffs and cracks, revealed by setBallCondition.
	let ballGold = false
	let lastCondition = 1
	const scuffMat = new THREE.MeshBasicNodeMaterial({
		color: ART.scuff,
		side: THREE.DoubleSide,
	})
	const crackMat = new THREE.MeshBasicNodeMaterial({
		color: PALETTE.ink,
		side: THREE.DoubleSide,
	})
	const scuffGeo = new THREE.CircleGeometry(BALL_R * 0.3, 6)
	const crackGeo = new THREE.PlaneGeometry(BALL_R * 0.1, BALL_R * 0.85)

	function surfacePatch(geo: THREE.BufferGeometry, mat: THREE.Material, i: number, n: number) {
		// Even-ish spread over the sphere, biased away from the face.
		const t = (i + 0.5) / n
		const y = 1 - 2 * t
		const r = Math.sqrt(Math.max(0, 1 - y * y))
		const a = i * 2.39996
		const nx = Math.cos(a) * r
		const nz = Math.abs(Math.sin(a) * r) * 0.9 + 0.1
		_vec.set(nx, y, nz).normalize()
		const m = new THREE.Mesh(geo, mat)
		m.position.copy(_vec).multiplyScalar(BALL_R * 1.008)
		m.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), _vec)
		m.rotateZ(a)
		m.scale.setScalar(0)
		ballSpin.add(m)
		return m
	}

	const scuffs: THREE.Mesh[] = []
	for (let i = 0; i < 6; i++) scuffs.push(surfacePatch(scuffGeo, scuffMat, i, 6))
	const cracks: THREE.Mesh[] = []
	for (let i = 0; i < 3; i++) cracks.push(surfacePatch(crackGeo, crackMat, i + 6, 9))

	const ballClean = new THREE.Color(PALETTE.ball)
	const ballDirty = new THREE.Color(ART.ballDirty)

	function updateBallShadow() {
		const p = ballRoot.position
		const h = Math.max(0, p.y - BALL_REST_Y)
		const lift = Math.min(h, 14)
		const s = BALL_R * (2 + h * 0.1)
		ballShadow.position.set(
			p.x + shadowDirX * lift * shadowSkew,
			ART.shadowY,
			p.z + shadowDirZ * lift * shadowSkew,
		)
		ballShadow.scale.set(s, 1, s * shadowStretch)
		ballShadowMat.opacity = ART.shadowOpacity / (1 + h * 0.08)
	}
	updateBallShadow()

	// ── Effects: pooled particles ────────────────────────────────────────────

	const POOL = ART.particlePool
	const pPos = new Float32Array(POOL * 3)
	const pVel = new Float32Array(POOL * 3)
	const pRot = new Float32Array(POOL * 3)
	const pSpin = new Float32Array(POOL * 3)
	const pLife = new Float32Array(POOL)
	const pMax = new Float32Array(POOL)
	const pSize = new Float32Array(POOL)
	const pGrav = new Float32Array(POOL)
	let pCursor = 0

	const particles = new THREE.InstancedMesh(
		new THREE.TetrahedronGeometry(0.62),
		new THREE.MeshBasicNodeMaterial({ color: 0xffffff }),
		POOL,
	)
	particles.frustumCulled = false
	_mat.makeScale(0, 0, 0)
	for (let i = 0; i < POOL; i++) {
		particles.setMatrixAt(i, _mat)
		particles.setColorAt(i, _color.set(0xffffff))
	}
	particles.instanceMatrix.needsUpdate = true
	scene.add(particles)

	function spawn(
		x: number,
		y: number,
		z: number,
		speed: number,
		bx: number,
		by: number,
		bz: number,
		size: number,
		life: number,
		grav: number,
		hex: number,
	) {
		const i = pCursor
		pCursor = (pCursor + 1) % POOL
		const i3 = i * 3
		// Random direction on the sphere, then the recipe's directional bias.
		const u = rng.range(-1, 1)
		const a = rng.range(0, Math.PI * 2)
		const r = Math.sqrt(Math.max(0, 1 - u * u))
		const sp = speed * rng.range(0.45, 1)
		pPos[i3] = x
		pPos[i3 + 1] = y
		pPos[i3 + 2] = z
		pVel[i3] = Math.cos(a) * r * sp + bx * rng.range(0.6, 1.4)
		pVel[i3 + 1] = u * sp * 0.7 + by * rng.range(0.6, 1.4)
		pVel[i3 + 2] = Math.sin(a) * r * sp + bz * rng.range(0.6, 1.4)
		pRot[i3] = rng.range(0, 6.28)
		pRot[i3 + 1] = rng.range(0, 6.28)
		pRot[i3 + 2] = rng.range(0, 6.28)
		pSpin[i3] = rng.range(-14, 14)
		pSpin[i3 + 1] = rng.range(-14, 14)
		pSpin[i3 + 2] = rng.range(-14, 14)
		const l = life * rng.range(0.7, 1.15)
		pLife[i] = l
		pMax[i] = l
		pSize[i] = size * rng.range(0.65, 1.35)
		pGrav[i] = grav
		particles.setColorAt(i, _color.set(hex))
		if (particles.instanceColor) particles.instanceColor.needsUpdate = true
	}

	function burst(kind: BurstKind, x: number, y: number, z: number) {
		const s = BURSTS[kind]
		for (let k = 0; k < s.n; k++) {
			spawn(
				x,
				y,
				z,
				s.speed,
				s.bias[0],
				s.bias[1],
				s.bias[2],
				s.size,
				s.life,
				s.grav,
				rng.pick(s.colors),
			)
		}
	}

	// ── Effects: flight trail ────────────────────────────────────────────────
	//
	// A cross of two ribbons on the same centreline, so the tracer stays visible
	// whatever angle the camera holds. Fixed-size buffers, rebuilt in place.

	const TN = ART.trailPoints
	const tPts = new Float32Array(TN * 3)
	const tPos = new Float32Array(TN * 4 * 3)
	const tNor = new Float32Array(TN * 4 * 3)
	const tCol = new Float32Array(TN * 4 * 3)
	const tIdx = new Uint16Array((TN - 1) * 12)
	for (let s = 0; s < 2; s++) {
		const base = s * TN * 2
		for (let i = 0; i < TN - 1; i++) {
			const o = (s * (TN - 1) + i) * 6
			const a = base + i * 2
			tIdx[o] = a
			tIdx[o + 1] = a + 1
			tIdx[o + 2] = a + 2
			tIdx[o + 3] = a + 1
			tIdx[o + 4] = a + 3
			tIdx[o + 5] = a + 2
		}
	}
	for (let i = 0; i < TN; i++) {
		const t = i / (TN - 1)
		_color.set(PALETTE.ball).lerp(_colorB.set(ART.waterEdge), 1 - t)
		for (let s = 0; s < 2; s++) {
			for (let k = 0; k < 2; k++) {
				const v = (s * TN + i) * 2 + k
				tCol[v * 3] = _color.r
				tCol[v * 3 + 1] = _color.g
				tCol[v * 3 + 2] = _color.b
				tNor[v * 3 + 1] = 1
			}
		}
	}
	const trailGeo = new THREE.BufferGeometry()
	trailGeo.setAttribute('position', new THREE.BufferAttribute(tPos, 3))
	trailGeo.setAttribute('normal', new THREE.BufferAttribute(tNor, 3))
	trailGeo.setAttribute('color', new THREE.BufferAttribute(tCol, 3))
	trailGeo.setIndex(new THREE.BufferAttribute(tIdx, 1))
	const trail = new THREE.Mesh(
		trailGeo,
		new THREE.MeshBasicNodeMaterial({
			vertexColors: true,
			transparent: true,
			opacity: 0.6,
			depthWrite: false,
			side: THREE.DoubleSide,
		}),
	)
	trail.frustumCulled = false
	trail.visible = false
	scene.add(trail)
	const trailPosAttr = trailGeo.getAttribute('position') as THREE.BufferAttribute

	function rebuildTrail() {
		for (let i = 0; i < TN; i++) {
			const i3 = i * 3
			const p3 = (i > 0 ? i - 1 : i) * 3
			const n3 = (i < TN - 1 ? i + 1 : i) * 3
			let dx = tPts[n3]! - tPts[p3]!
			let dy = tPts[n3 + 1]! - tPts[p3 + 1]!
			let dz = tPts[n3 + 2]! - tPts[p3 + 2]!
			const dl = Math.hypot(dx, dy, dz)
			if (dl < 1e-5) {
				dx = 0
				dy = 0
				dz = 1
			} else {
				dx /= dl
				dy /= dl
				dz /= dl
			}
			let sx = -dz
			let sz = dx
			const sl = Math.hypot(sx, sz)
			if (sl < 1e-5) {
				sx = 1
				sz = 0
			} else {
				sx /= sl
				sz /= sl
			}
			const ux = -sz * dy
			const uy = sz * dx - sx * dz
			const uz = sx * dy
			const ul = Math.hypot(ux, uy, uz) || 1
			const w = ART.trailWidth * 0.5 * ((i + 1) / TN) ** 0.8
			const px = tPts[i3]!
			const py = tPts[i3 + 1]!
			const pz = tPts[i3 + 2]!
			const a = i * 2
			const b = TN * 2 + i * 2
			tPos[a * 3] = px - sx * w
			tPos[a * 3 + 1] = py
			tPos[a * 3 + 2] = pz - sz * w
			tPos[(a + 1) * 3] = px + sx * w
			tPos[(a + 1) * 3 + 1] = py
			tPos[(a + 1) * 3 + 2] = pz + sz * w
			tPos[b * 3] = px - (ux / ul) * w
			tPos[b * 3 + 1] = py - (uy / ul) * w
			tPos[b * 3 + 2] = pz - (uz / ul) * w
			tPos[(b + 1) * 3] = px + (ux / ul) * w
			tPos[(b + 1) * 3 + 1] = py + (uy / ul) * w
			tPos[(b + 1) * 3 + 2] = pz + (uz / ul) * w
		}
		trailPosAttr.needsUpdate = true
	}

	function clearTrail() {
		const p = ballRoot.position
		for (let i = 0; i < TN; i++) {
			tPts[i * 3] = p.x
			tPts[i * 3 + 1] = p.y
			tPts[i * 3 + 2] = p.z
		}
		rebuildTrail()
	}
	clearTrail()

	// ── Golfer ───────────────────────────────────────────────────────────────
	//
	// The sim owns the club. `setSwingAngle` does nothing but rotate the arm
	// assembly about +X at PIVOT, with the club along local -Y and length
	// CLUB_LEN, which is exactly the arc the strike resolver integrates.

	const GOLFER_X = 0.66
	const FACING = Math.PI / 2

	const golferRoot = new THREE.Object3D()
	scene.add(golferRoot)

	const bodyPivot = new THREE.Object3D()
	bodyPivot.position.set(GOLFER_X, 0, PIVOT.z)
	bodyPivot.rotation.y = FACING
	golferRoot.add(bodyPivot)

	const body = new THREE.Object3D()
	bodyPivot.add(body)

	const poloMat = lambert(0xffffff)
	const capMat = new THREE.MeshLambertNodeMaterial({
		color: 0xffffff,
		flatShading: true,
		side: THREE.DoubleSide,
	})
	const skinMat = lambert(ART.skin)
	const trouserMat = lambert(ART.trousers)
	const shoeMat = lambert(ART.shoe)
	const inkMat = lambert(PALETTE.ink, false)
	const creamMat = lambert(PALETTE.ball, false)

	const legGeo = new THREE.CapsuleGeometry(0.105, 0.5, 3, 8)
	const legs: THREE.Mesh[] = []
	for (const s of [-1, 1]) {
		const leg = new THREE.Mesh(legGeo, trouserMat)
		leg.position.set(s * 0.15, 0.4, 0)
		body.add(leg)
		legs.push(leg)
		body.add(box(0.17, 0.075, 0.3, s * 0.16, 0.04, -0.06, shoeMat))
	}

	const torso = box(0.44, 0.68, 0.3, 0, 1.07, 0, poloMat)
	body.add(torso)
	const neck = box(0.12, 0.11, 0.12, 0, 1.44, -0.01, skinMat)
	body.add(neck)

	const headGroup = new THREE.Object3D()
	headGroup.position.set(0, 1.575, -0.02)
	body.add(headGroup)
	headGroup.add(new THREE.Mesh(new THREE.IcosahedronGeometry(0.155, 1), skinMat))
	const nose = new THREE.Mesh(new THREE.ConeGeometry(0.035, 0.08, 5), skinMat)
	nose.position.set(0, -0.01, -0.15)
	nose.rotation.x = -Math.PI / 2
	headGroup.add(nose)
	for (const s of [-1, 1]) {
		const ear = new THREE.Mesh(new THREE.IcosahedronGeometry(0.042, 0), skinMat)
		ear.position.set(s * 0.15, -0.01, 0.01)
		headGroup.add(ear)
	}

	const capGroup = new THREE.Object3D()
	const CAP_HOME = new THREE.Vector3(0, 0.085, 0.008)
	capGroup.position.copy(CAP_HOME)
	headGroup.add(capGroup)
	const crown = new THREE.Mesh(
		new THREE.SphereGeometry(0.168, 10, 6, 0, Math.PI * 2, 0, Math.PI * 0.56),
		capMat,
	)
	capGroup.add(crown)
	const bill = box(0.21, 0.028, 0.17, 0, -0.004, -0.16, capMat)
	bill.rotation.x = 0.14
	capGroup.add(bill)

	const hatShadow = blob(ART.shadowOpacity * 0.8)
	hatShadow.scale.set(0.22, 1, 0.2)
	hatShadow.visible = false
	golferRoot.add(hatShadow)

	const golferShadow = blob(ART.shadowOpacity * 0.9)
	golferShadow.position.set(GOLFER_X, ART.shadowY, PIVOT.z)
	golferShadow.scale.set(0.5, 1, 0.42)
	golferRoot.add(golferShadow)

	// Arm assembly. clubWrap sits on PIVOT and stays identity except during a
	// tantrum, so the club is authoritative whenever the swing matters.
	const clubWrap = new THREE.Object3D()
	clubWrap.position.set(PIVOT.x, PIVOT.y, PIVOT.z)
	golferRoot.add(clubWrap)

	// Static bridge from the chest to the hands (the hands are the pivot).
	clubWrap.add(
		strut(
			0.075,
			0.095,
			new THREE.Vector3(0, 0, 0),
			new THREE.Vector3(GOLFER_X - 0.06, 0.1, 0),
			skinMat,
		),
	)
	const sleeve = strut(
		0.1,
		0.12,
		new THREE.Vector3(GOLFER_X - 0.3, 0.06, 0),
		new THREE.Vector3(GOLFER_X - 0.02, 0.1, 0),
		poloMat,
	)
	clubWrap.add(sleeve)
	clubWrap.add(box(0.11, 0.11, 0.12, 0, 0.005, 0, creamMat))

	const clubRoot = new THREE.Object3D()
	clubWrap.add(clubRoot)

	const faceLocal = new THREE.Vector3(0, FACE_LOCAL_Y, FACE_LOCAL_Z)
	const clubMat = lambert(ART.club, false)
	clubRoot.add(strut(0.02, 0.014, new THREE.Vector3(0, 0, 0), faceLocal, clubMat))
	// Grip: the top seventh of the club's reach, measured along the drawn shaft.
	const gripFrac = CLUB_LEN / 7 / faceLocal.length()
	clubRoot.add(
		strut(
			0.032,
			0.028,
			new THREE.Vector3(0, 0, 0),
			new THREE.Vector3(0, FACE_LOCAL_Y * gripFrac, FACE_LOCAL_Z * gripFrac),
			inkMat,
		),
	)

	// Counter-rotated so the head is square to the world at impact: the drawn
	// face is then exactly CLUB_FACE_W x CLUB_FACE_H around (0, SWEET_Y, 0), and
	// its lower edge clears a fully crouched ball by a visible margin.
	const clubHead = new THREE.Object3D()
	clubHead.position.copy(faceLocal)
	clubHead.rotation.x = -SWING_ANGLE_IMPACT
	clubRoot.add(clubHead)
	clubHead.add(box(CLUB_FACE_W, CLUB_FACE_H, 0.085, 0, 0, 0, clubMat))
	for (const s of [-1, 1]) {
		clubHead.add(box(CLUB_FACE_W * 0.86, CLUB_FACE_H * 0.8, 0.012, 0, 0, s * 0.047, creamMat))
	}

	// Rig state.
	let angerTier = 0
	let hatDropped = false
	let swingAngle = 0
	let tantrum = 0
	let steamOn = false
	let steamClock = 0
	const skinBase = new THREE.Color(ART.skin)
	const skinFlush = new THREE.Color(ART.skinFlush)

	function applyPose() {
		// The club stays literal; the body just sells the turn.
		const env = Math.sin(clamp01(tantrum) * Math.PI)
		const wob = Math.sin(tantrum * Math.PI * 9)
		const turn = clamp(-swingAngle * 0.1, -0.36, 0.36)
		body.rotation.y = turn + wob * 0.3 * env
		body.rotation.z = wob * 0.14 * env
		body.position.set(0, Math.abs(Math.sin(tantrum * Math.PI * 4)) * 0.11 * env, 0)
		body.rotation.x = -0.06 - env * 0.12
		clubWrap.rotation.set(-0.7 * env, 0, -wob * 0.55 * env)
	}

	function applyCap() {
		if (hatDropped) return
		if (angerTier <= 0) capGroup.rotation.set(0, 0, 0)
		else if (angerTier === 1) capGroup.rotation.set(-0.44, 0.18, 0.09)
		else capGroup.rotation.set(0.3, Math.PI, -0.07)
	}

	const golfer: GolferRig = {
		root: golferRoot,

		setSpec(spec: GolferSpec) {
			poloMat.color.set(spec.color)
			capMat.color.set(spec.color).multiplyScalar(0.72)
			const b = BUILDS[spec.build] ?? BUILDS[1]
			torso.scale.set(b.w, b.h, b.d)
			for (const leg of legs) leg.scale.set(b.hip, 1, b.hip)
			const lift = (b.h - 1) * 0.34
			neck.position.y = 1.44 + lift
			headGroup.position.y = 1.575 + lift
		},

		setSwingAngle(a: number) {
			clubRoot.rotation.x = a
			swingAngle = a
			applyPose()
		},

		setAnger(tier: number) {
			angerTier = clamp(Math.round(tier), 0, MAX_ANGER)
			applyCap()
			// Tier 3 starts a flush on the skin, tier 4 puts steam on it.
			const flush = angerTier >= 3 ? (angerTier - 2) / Math.max(1, MAX_ANGER - 2) : 0
			skinMat.color.copy(skinBase).lerp(skinFlush, clamp01(flush))
			steamOn = angerTier >= MAX_ANGER
		},

		dropHat(at: { x: number; z: number }) {
			if (hatDropped) return
			hatDropped = true
			headGroup.remove(capGroup)
			golferRoot.add(capGroup)
			capGroup.position.set(at.x, 0.055, at.z)
			capGroup.rotation.set(-Math.PI * 0.46, 0.8 + at.x * 0.35, 0.3)
			hatShadow.position.set(at.x, ART.shadowY, at.z)
			hatShadow.visible = true
			burst('turf', at.x, 0.06, at.z)
		},

		resetProps() {
			if (hatDropped) {
				golferRoot.remove(capGroup)
				headGroup.add(capGroup)
				capGroup.position.copy(CAP_HOME)
				hatShadow.visible = false
				hatDropped = false
			}
			angerTier = 0
			steamOn = false
			tantrum = 0
			capGroup.rotation.set(0, 0, 0)
			skinMat.color.copy(skinBase)
			applyPose()
		},

		setTantrum(t01: number) {
			tantrum = clamp01(t01)
			applyPose()
		},
	}

	golfer.setSpec({
		id: 'default',
		name: 'default',
		speedMult: 1,
		dispersionDeg: 0,
		sweetSpotMult: 1,
		temperMult: 1,
		tempoMult: 1,
		chaos: 0,
		color: 0x9fd8c0,
		build: 1,
	})
	golfer.setSwingAngle(0)

	// ── Daylight ─────────────────────────────────────────────────────────────

	const skyTopDay = new THREE.Color(PALETTE.skyTop)
	const skyTopDusk = new THREE.Color(ART.skyTopDusk)
	const skyLowDay = new THREE.Color(PALETTE.skyLow)
	const skyLowDusk = new THREE.Color(ART.skyLowDusk)
	const curTop = new THREE.Color()
	const curLow = new THREE.Color()
	const sunDay = new THREE.Color(ART.sunColorDay)
	const sunDusk = new THREE.Color(ART.sunColorDusk)
	let gradPow = 0.35
	let lastDaylight = -1

	function skyAt(h: number, out: THREE.Color) {
		const b = h <= 0 ? 0 : Math.min(1, h) ** gradPow
		out.copy(curLow).lerp(curTop, b)
		if (h < 0) out.multiplyScalar(1 + Math.max(h, -1) * 0.32)
		return out
	}

	function setDaylight(t01: number) {
		const t = clamp01(t01)
		if (Math.abs(t - lastDaylight) < 0.0015) return
		lastDaylight = t

		// The gradient's warm band climbs the dome as the day burns down.
		gradPow = mix(0.35, 1.55, t)
		curTop.copy(skyTopDay).lerp(skyTopDusk, t)
		curLow.copy(skyLowDay).lerp(skyLowDusk, t)

		const arr = skyCol.array as Float32Array
		for (let i = 0; i < skyPos.count; i++) {
			skyAt(skyPos.getY(i) / ART.skyRadius, _color)
			arr[i * 3] = _color.r
			arr[i * 3 + 1] = _color.g
			arr[i * 3 + 2] = _color.b
		}
		skyCol.needsUpdate = true

		// Fog, clear colour and hemisphere fill all follow the horizon band, so
		// distant ground melts into the sky instead of ending at a hard line.
		skyAt(0.1, _color)
		fog.color.copy(_color)
		clearColor.copy(_color)
		fog.near = mix(ART.fogNearDay, ART.fogNearDusk, t)
		fog.far = mix(ART.fogFarDay, ART.fogFarDusk, t)

		hemi.color.copy(curTop)
		hemi.groundColor.set(PALETTE.rough).lerp(_colorB.set(0x8a7a54), t)
		hemi.intensity = mix(ART.hemiIntensityDay, ART.hemiIntensityDusk, t)

		// The sun drops, swings left and goes copper. Long, warm and low.
		sun.color.copy(sunDay).lerp(sunDusk, t)
		sun.intensity = mix(ART.sunIntensityDay, ART.sunIntensityDusk, t)
		sun.position.set(mix(-46, -132, t), mix(86, 15, t), mix(40, -44, t))

		// Blob shadows lengthen and lean away from the sun. Stylised, not solved.
		const hx = -sun.position.x
		const hz = -sun.position.z
		const hl = Math.hypot(hx, hz) || 1
		shadowDirX = hx / hl
		shadowDirZ = hz / hl
		shadowSkew = mix(0.06, 0.5, t)
		shadowStretch = mix(1, 2.4, t)
		golferShadow.scale.set(0.5, 1, 0.42 * shadowStretch)
		golferShadow.position.set(
			GOLFER_X + shadowDirX * 0.3 * shadowSkew * 6,
			ART.shadowY,
			PIVOT.z + shadowDirZ * 0.3 * shadowSkew * 6,
		)
		updateBallShadow()
	}
	setDaylight(0)

	// ── Per-frame effects, all on unscaled time ──────────────────────────────

	let flagClock = 0

	function stepEffects(dtUnscaled: number) {
		const dt = Math.min(Math.max(dtUnscaled, 0), 0.1)

		flagClock += dt
		if (bannerPivot && banner) {
			// The banner is built along local +X, so pointing it downwind is the
			// wind bearing turned back a quarter turn. Everything else — how far it
			// lifts off the pole and how hard it ripples — is the speed.
			const gust = Math.sin(flagClock * (1.3 + 3.2 * windAmp))
			bannerPivot.rotation.y = windRad - Math.PI / 2 + gust * 0.22 * windAmp
			banner.rotation.z =
				-ART.flagDroop * (1 - windAmp) + Math.sin(flagClock * (2.1 + 2.6 * windAmp) + 1) * 0.13
		}

		if (steamOn) {
			steamClock += dt
			while (steamClock >= ART.steamPeriod) {
				steamClock -= ART.steamPeriod
				headGroup.getWorldPosition(_vec)
				spawn(
					_vec.x + rng.range(-0.14, 0.14),
					_vec.y + 0.2,
					_vec.z + rng.range(-0.14, 0.14),
					0.5,
					0,
					1.1,
					0,
					0.07,
					0.9,
					-3.2,
					rng.pick(STEAM_COLORS),
				)
			}
		}

		let dirty = false
		for (let i = 0; i < POOL; i++) {
			const life = pLife[i]!
			if (life <= 0) continue
			dirty = true
			const i3 = i * 3
			const nl = life - dt
			if (nl <= 0) {
				pLife[i] = 0
				_mat.makeScale(0, 0, 0)
				particles.setMatrixAt(i, _mat)
				continue
			}
			pLife[i] = nl

			const g = pGrav[i]!
			let vx = pVel[i3]!
			let vy = pVel[i3 + 1]! - g * dt
			let vz = pVel[i3 + 2]!
			const x = pPos[i3]! + vx * dt
			let y = pPos[i3 + 1]! + vy * dt
			const z = pPos[i3 + 2]! + vz * dt
			if (g > 0 && y < 0.02 && vy < 0) {
				y = 0.02
				vy = -vy * 0.34
				vx *= 0.68
				vz *= 0.68
			}
			pVel[i3] = vx
			pVel[i3 + 1] = vy
			pVel[i3 + 2] = vz
			pPos[i3] = x
			pPos[i3 + 1] = y
			pPos[i3 + 2] = z

			const rx = pRot[i3]! + pSpin[i3]! * dt
			const ry = pRot[i3 + 1]! + pSpin[i3 + 1]! * dt
			const rz = pRot[i3 + 2]! + pSpin[i3 + 2]! * dt
			pRot[i3] = rx
			pRot[i3 + 1] = ry
			pRot[i3 + 2] = rz

			const u = nl / pMax[i]!
			const s = pSize[i]! * (0.35 + 0.65 * u) * Math.min(1, u * 4)
			_quat.setFromEuler(_euler.set(rx, ry, rz))
			_mat.compose(_vecB.set(x, y, z), _quat, _scale.setScalar(s))
			particles.setMatrixAt(i, _mat)
		}
		if (dirty) particles.instanceMatrix.needsUpdate = true
	}

	buildHole(COURSE[0]!)

	// ── Handles ──────────────────────────────────────────────────────────────

	return {
		scene,
		ball: ballRoot,
		golfer,
		flag,
		buildHole,

		setBallPos(x: number, y: number, z: number) {
			ballRoot.position.set(x, y, z)
			updateBallShadow()
		},

		setBallSpin(spinRad: number, axis: { x: number; y: number; z: number }) {
			_vec.set(axis.x, axis.y, axis.z)
			if (_vec.lengthSq() < 1e-9) _vec.set(1, 0, 0)
			ballSpin.quaternion.setFromAxisAngle(_vec.normalize(), spinRad)
		},

		setBallSquash(amount: number) {
			const a = clamp(amount, -0.85, 0.9)
			ballSquash.scale.set(1 + a * 0.45, 1 - a * 0.55, 1 + a * 0.45)
		},

		setRigAt(x: number, z: number, onTee: boolean) {
			golferRoot.position.set(x, 0, z)
			peg.position.set(x, TEE_TOP_Y / 2, z)
			peg.visible = onTee
		},

		setBallGold(on: boolean) {
			ballGold = on
			this.setBallCondition(lastCondition)
		},

		setBallCondition(hp01: number) {
			const h = clamp01(hp01)
			lastCondition = h
			ballMat.color.copy(ballClean).lerp(ballDirty, 1 - h)
			// Cosmetic and always will be. The dream does not become a stat bonus.
			if (ballGold) ballMat.color.lerp(GOLD, 0.42)
			const scuffStart = SCUFFED_AT / HP_MAX
			for (let i = 0; i < scuffs.length; i++) {
				const thr = scuffStart * (1 - i / scuffs.length)
				scuffs[i]!.scale.setScalar(clamp01((thr - h) / 0.07))
			}
			const crackStart = CRACKED_AT / HP_MAX
			for (let i = 0; i < cracks.length; i++) {
				const thr = crackStart - i * (crackStart / cracks.length)
				cracks[i]!.scale.setScalar(clamp01((thr - h) / 0.05))
			}
			// The brows do the rest of the talking. There is no mouth.
			for (let i = 0; i < brows.length; i++) {
				const s = i === 0 ? -1 : 1
				brows[i]!.rotation.z = s * (0.3 + (1 - h) * 0.45)
				brows[i]!.position.y = BALL_R * (0.52 - (1 - h) * 0.06)
			}
		},

		setDaylight,

		setTrailVisible(v: boolean) {
			trail.visible = v
		},

		pushTrail(x: number, y: number, z: number) {
			tPts.copyWithin(0, 3)
			const o = (TN - 1) * 3
			tPts[o] = x
			tPts[o + 1] = y
			tPts[o + 2] = z
			rebuildTrail()
		},

		clearTrail,
		burst,
		stepEffects,
	}
}
